// ════════════════════════════════════════════════════════════════════════════
//  Dashboard Executivo — todos os indicadores vêm do agregado único
//  GET /api/dashboard/executivo (agregação no SQL; Fase 4c do plano
//  docs/roadmaps/PLANO_DASHBOARD_CONFIGURAVEL.md). Nada é calculado a partir
//  das listagens completas de CP/CR no navegador.
// ════════════════════════════════════════════════════════════════════════════

import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle } from "lucide-react";
import { executivoService } from "@/features/dashboard/services/executivoService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const fmtMoeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const DashboardExecutivo = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dashboard-executivo"],
    queryFn: executivoService.get,
    staleTime: 60_000,
  });

  if (isLoading || !data) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Executivo</h1>
          <p className="text-muted-foreground mt-1">
            Visão geral dos principais indicadores financeiros
          </p>
        </div>
        {isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="ml-2">
              Não foi possível carregar os indicadores. Tente novamente mais tarde.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6">
            <Skeleton className="h-40 w-full rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-lg" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-80 w-full rounded-lg" />
              <Skeleton className="h-80 w-full rounded-lg" />
            </div>
          </div>
        )}
      </div>
    );
  }

  const { saude, mesCorrente, pontualidade, evolucaoTrimestral, despesasPorGrupo, fluxoDiario } = data;

  const evolucaoData = evolucaoTrimestral.map((m) => ({
    mes: format(new Date(m.ano, m.mes - 1, 1), "MMM/yy", { locale: ptBR }),
    entradas: m.entradas,
    saidas: m.saidas,
    saldo: m.saldo,
  }));

  const despesasData = despesasPorGrupo.map((d) => ({ categoria: d.grupo, valor: d.valor }));

  // "yyyy-MM-dd" → "dd/MM" sem passar por Date (evita deslocamento de fuso).
  const fluxoData = fluxoDiario.map((f) => ({
    date: `${f.dia.slice(8, 10)}/${f.dia.slice(5, 7)}`,
    entradas: f.entradas,
    saidas: f.saidas,
  }));

  const saudeFinanceira = saude.saldoPrevisto > 0 && saude.contasAtrasadas < 5 && saude.taxaInadimplencia < 10
    ? "Excelente"
    : saude.saldoPrevisto > 0 && saude.contasAtrasadas < 10
    ? "Boa"
    : saude.saldoPrevisto < 0 || saude.contasAtrasadas > 15
    ? "Crítica"
    : "Regular";

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dashboard Executivo</h1>
        <p className="text-muted-foreground mt-1">
          Visão geral dos principais indicadores financeiros
        </p>
      </div>

      {/* Saúde Financeira */}
      <Card className="border-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Saúde Financeira</CardTitle>
              <CardDescription>Análise geral do status financeiro atual</CardDescription>
            </div>
            <Badge variant={saudeFinanceira === "Excelente" || saudeFinanceira === "Boa" ? "default" : "destructive"} className="text-lg px-4 py-2">
              {saudeFinanceira}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              {saude.saldoPrevisto >= 0 ? (
                <CheckCircle className="h-8 w-8 text-green-500" />
              ) : (
                <AlertCircle className="h-8 w-8 text-red-500" />
              )}
              <div>
                <p className="text-sm text-muted-foreground">Saldo Previsto</p>
                <p className={`text-xl font-bold ${saude.saldoPrevisto >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {fmtMoeda(saude.saldoPrevisto)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {saude.contasAtrasadas < 5 ? (
                <CheckCircle className="h-8 w-8 text-green-500" />
              ) : (
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              )}
              <div>
                <p className="text-sm text-muted-foreground">Contas Atrasadas</p>
                <p className="text-xl font-bold text-foreground">{saude.contasAtrasadas}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {saude.taxaInadimplencia < 10 ? (
                <CheckCircle className="h-8 w-8 text-green-500" />
              ) : (
                <AlertCircle className="h-8 w-8 text-red-500" />
              )}
              <div>
                <p className="text-sm text-muted-foreground">Taxa de Inadimplência</p>
                <p className="text-xl font-bold text-foreground">{saude.taxaInadimplencia.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Principais Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Entradas do Mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {fmtMoeda(mesCorrente.entradas)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Ticket médio: {fmtMoeda(mesCorrente.ticketMedioRecebimento)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Saídas do Mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {fmtMoeda(mesCorrente.saidas)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Ticket médio: {fmtMoeda(mesCorrente.ticketMedioPagamento)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pontualidade Recebimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{pontualidade.taxaRecebimentoNoPrazo.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Prazo médio: {Math.abs(pontualidade.prazoMedioRecebimentoDias)} dias {pontualidade.prazoMedioRecebimentoDias > 0 ? 'após' : 'antes'} vencimento
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pontualidade Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{pontualidade.taxaPagamentoNoPrazo.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Prazo médio: {Math.abs(pontualidade.prazoMedioPagamentoDias)} dias {pontualidade.prazoMedioPagamentoDias > 0 ? 'após' : 'antes'} vencimento
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alertas Importantes */}
      {(saude.saldoPrevisto < 0 || saude.contasAtrasadas > 0 || saude.emAbertoPagar > saude.emAbertoReceber) && (
        <Alert variant={saude.saldoPrevisto < 0 ? "destructive" : "default"}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <strong>Atenção:</strong>{" "}
            {saude.saldoPrevisto < 0 && "Saldo previsto negativo. "}
            {saude.contasAtrasadas > 0 && `${saude.contasAtrasadas} conta(s) atrasada(s). `}
            {saude.emAbertoPagar > saude.emAbertoReceber && "Contas a pagar superam contas a receber."}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolução Trimestral */}
        <Card>
          <CardHeader>
            <CardTitle>Evolução Trimestral</CardTitle>
            <CardDescription>Comparação dos últimos 3 meses</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={evolucaoData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="mes" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [fmtMoeda(value)]}
                />
                <Legend />
                <Bar dataKey="entradas" fill="hsl(var(--chart-1))" name="Entradas" radius={[8, 8, 0, 0]} />
                <Bar dataKey="saidas" fill="hsl(var(--chart-2))" name="Saídas" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Distribuição de Despesas */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Despesas</CardTitle>
            <CardDescription>Principais grupos de despesas do mês</CardDescription>
          </CardHeader>
          <CardContent>
            {despesasData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={despesasData}
                    dataKey="valor"
                    nameKey="categoria"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    isAnimationActive={false}
                    label={(entry) => mesCorrente.saidas > 0
                      ? `${entry.categoria}: ${((entry.valor / mesCorrente.saidas) * 100).toFixed(0)}%`
                      : entry.categoria}
                  >
                    {despesasData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [fmtMoeda(value), "Valor"]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhuma despesa registrada
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tendência do Fluxo */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Tendência do Fluxo de Caixa</CardTitle>
            <CardDescription>Evolução diária do mês atual</CardDescription>
          </CardHeader>
          <CardContent>
            {fluxoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={fluxoData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => [fmtMoeda(value)]}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="entradas" stroke="hsl(var(--chart-1))" strokeWidth={2} name="Entradas" />
                  <Line type="monotone" dataKey="saidas" stroke="hsl(var(--chart-2))" strokeWidth={2} name="Saídas" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Nenhum dado de fluxo disponível
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardExecutivo;
